import { Minus, Plus, Save, Settings2, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { AddProductDialog } from '@/components/admin/AddProductDialog';
import { ApiSubItemsDialog } from '@/components/admin/ApiSubItemsDialog';
import { Button, Card, ConfirmButton, NumberStepper, useToast } from '@/components/ui';
import {
  useDeleteProduct,
  useProductSettings,
  useUpdateProductSettings,
} from '@/hooks/usePriceSettings';
import {
  getAllowedTermYears,
  isApiBundleProductCode,
  isTermBasedWebsiteCode,
  WEBSITE_PRODUCT_CODE_PREFIX,
} from '@/utils/productTermPricing';

const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';
const STEP = 500;

interface PriceStepperProps {
  value: number;
  onChange: (val: number) => void;
  ariaLabel: string;
}

const PriceStepper = ({ value, onChange, ariaLabel }: PriceStepperProps) => (
  <div className="inline-flex items-center rounded-lg border border-line bg-white shadow-sm">
    <button
      type="button"
      aria-label={`Decrease ${ariaLabel}`}
      onClick={() => onChange(Math.max(0, value - STEP))}
      className="flex h-8 w-8 items-center justify-center rounded-l-lg border-r border-line text-ink/60 transition-colors hover:bg-cream active:bg-line disabled:opacity-40"
      disabled={value <= 0}
    >
      <Minus className="h-3.5 w-3.5" />
    </button>
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => {
        // Allow one decimal point (ADKJ-124: a product's price can be
        // fractional, e.g. "Website (AI based)" at 3093.2203) - stripping
        // every non-digit, as this used to, made a decimal price uneditable.
        const cleaned = e.target.value.replace(/[^\d.]/g, '');
        const n = parseFloat(cleaned);
        if (!Number.isNaN(n)) onChange(n);
        else if (cleaned === '') onChange(0);
      }}
      className="w-24 border-none bg-transparent py-1.5 text-center text-sm font-medium text-ink focus:outline-none focus:ring-0"
      aria-label={ariaLabel}
    />
    <button
      type="button"
      aria-label={`Increase ${ariaLabel}`}
      onClick={() => onChange(value + STEP)}
      className="flex h-8 w-8 items-center justify-center rounded-r-lg border-l border-line text-ink/60 transition-colors hover:bg-cream active:bg-line"
    >
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

const AdminPriceSettingsPage = () => {
  const { data, isLoading } = useProductSettings();
  const update = useUpdateProductSettings();
  const remove = useDeleteProduct();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  // Seeds the add-product dialog's code box. "Add website tier" prefills the
  // WEBSITE_ prefix, which is what makes the new product behave as a website
  // tier rather than an ordinary flat product.
  const [addInitialCode, setAddInitialCode] = useState('');

  const [discountPercent, setDiscountPercent] = useState('50');
  const [fixedPrices, setFixedPrices] = useState<Record<string, number>>({});
  // Upgrade-ladder rank per AUM product, as typed. Kept as strings so the field
  // can be cleared (empty = not part of the ladder) rather than snapping to 0.
  const [tiers, setTiers] = useState<Record<string, string>>({});
  // Which API bundle product's item-catalogue dialog is open (null = none).
  const [manageItemsCode, setManageItemsCode] = useState<string | null>(null);
  // Price options per website tier, as typed (strings so a field can be
  // mid-edit without snapping to a number). Keyed by tier product code.
  const [websiteSlabs, setWebsiteSlabs] = useState<Record<string, string[]>>({});
  // Per-product override of the years-2+ multi-year discount (ADKJ-123), as
  // typed. Kept as strings so the field can be BLANK (= follow the
  // company-wide setting above) rather than snapping to a number; a code
  // absent from this map has no override, same meaning as blank.
  const [multiYearDiscounts, setMultiYearDiscounts] = useState<Record<string, string>>({});

  const catalogProducts = useMemo(
    () =>
      [...(data?.fixedProducts ?? []), ...(data?.aumProducts ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [data?.fixedProducts, data?.aumProducts],
  );
  const bandTemplate = useMemo(() => data?.aumBandTemplate ?? [], [data?.aumBandTemplate]);

  useEffect(() => {
    if (!data) return;
    setDiscountPercent(String(data.multiYearRenewalDiscountPercent));
    const prices: Record<string, number> = {};
    data.fixedProducts.forEach((p) => {
      prices[p.code] = p.price;
    });
    setFixedPrices(prices);
    const nextTiers: Record<string, string> = {};
    data.aumProducts.forEach((p) => {
      nextTiers[p.code] = p.tier != null ? String(p.tier) : '';
    });
    setTiers(nextTiers);
    const nextMultiYear: Record<string, string> = {};
    data.fixedProducts.forEach((p) => {
      if (p.multiYearDiscountPercent != null) {
        nextMultiYear[p.code] = String(p.multiYearDiscountPercent);
      }
    });
    setMultiYearDiscounts(nextMultiYear);
    // WEBSITE_CUSTOM never carries options (free-text in the wizard), and a
    // term-based tier (ADKJ-124, e.g. WEBSITE_AI) is priced from its own
    // Annual Price row x years, not from a chip list - it never belongs here
    // either (the server no longer sends it, but skip it defensively).
    const nextSlabs: Record<string, string[]> = {};
    Object.entries(data.websitePriceSlabs ?? {}).forEach(([code, slabs]) => {
      if (code === 'WEBSITE_CUSTOM' || isTermBasedWebsiteCode(code)) return;
      nextSlabs[code] = slabs.map((n) => String(n));
    });
    setWebsiteSlabs(nextSlabs);
  }, [data]);

  const setProductPrice = (code: string, price: number) => {
    setFixedPrices((prev) => ({ ...prev, [code]: price }));
  };

  const setProductTier = (code: string, tier: string) => {
    setTiers((prev) => ({ ...prev, [code]: tier }));
  };

  const setProductMultiYearDiscount = (code: string, value: string) => {
    setMultiYearDiscounts((prev) => ({ ...prev, [code]: value }));
  };

  const setSlabValue = (code: string, index: number, value: string) => {
    setWebsiteSlabs((prev) => ({
      ...prev,
      [code]: (prev[code] ?? []).map((s, i) => (i === index ? value : s)),
    }));
  };

  const addSlab = (code: string) => {
    setWebsiteSlabs((prev) => ({ ...prev, [code]: [...(prev[code] ?? []), ''] }));
  };

  const removeSlab = (code: string, index: number) => {
    setWebsiteSlabs((prev) => ({
      ...prev,
      [code]: (prev[code] ?? []).filter((_, i) => i !== index),
    }));
  };

  const onDeleteProduct = (code: string, name: string): void => {
    remove.mutate(code, {
      onSuccess: () => {
        toast.success(`${name} removed from catalogue`);
        setFixedPrices((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
      },
      onError: (err) => toast.error(err.message || 'Could not delete product'),
    });
  };

  const onSubmit = (): void => {
    const parsedDiscount = Number(discountPercent);
    if (!Number.isFinite(parsedDiscount) || parsedDiscount < 0 || parsedDiscount > 100) {
      toast.error('Multi-year discount must be between 0 and 100');
      return;
    }

    const fixedProducts = (data?.fixedProducts ?? []).map((p) => ({
      code: p.code,
      price: fixedPrices[p.code] ?? p.price,
    }));
    const invalid = fixedProducts.find((p) => !Number.isFinite(p.price) || p.price < 0);
    if (invalid) {
      const name = data?.fixedProducts.find((p) => p.code === invalid.code)?.name ?? invalid.code;
      toast.error(`Invalid price for ${name}`);
      return;
    }

    // Tier is only meaningful on AUM products; a blank field clears it.
    const tierRows: { code: string; tier: number | null }[] = [];
    const aumProducts = data?.aumProducts ?? [];
    for (let i = 0; i < aumProducts.length; i += 1) {
      const p = aumProducts[i];
      const raw = (tiers[p.code] ?? '').trim();
      const parsed = raw === '' ? null : Number(raw);
      if (parsed !== null && (!Number.isInteger(parsed) || parsed < 1 || parsed > 99)) {
        toast.error(`Tier for ${p.name} must be a whole number between 1 and 99`);
        return;
      }
      if (parsed !== (p.tier ?? null)) tierRows.push({ code: p.code, tier: parsed });
    }

    const duplicate = tierRows.find(
      (row, i) => row.tier != null && tierRows.findIndex((o) => o.tier === row.tier) !== i,
    );
    if (duplicate) {
      toast.error(`Tier ${duplicate.tier} is used by more than one plan`);
      return;
    }

    // Per-product multi-year discount override (ADKJ-123): only meaningful on
    // a flat, multi-year-capable product. A blank field clears the override
    // (falls back to the company-wide setting above). Collected into a map
    // rather than a separate row array: every fixed product already gets ONE
    // row below (for price), and merging into that same row keeps one row per
    // code instead of sending two separate patches for the same product.
    const multiYearByCode = new Map<string, number | null>();
    const multiYearEligible = (data?.fixedProducts ?? []).filter((p) =>
      getAllowedTermYears(p.code).some((years) => years > 1));
    for (let i = 0; i < multiYearEligible.length; i += 1) {
      const p = multiYearEligible[i];
      const raw = (multiYearDiscounts[p.code] ?? '').trim();
      const parsed = raw === '' ? null : Number(raw);
      if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > 100)) {
        toast.error(`Multi-year discount for ${p.name} must be between 0 and 100`);
        return;
      }
      if (parsed !== (p.multiYearDiscountPercent ?? null)) {
        multiYearByCode.set(p.code, parsed);
      }
    }

    // Website price options: every entry must be a positive whole rupee amount
    // and each tier keeps at least one option (the wizard chip row cannot be
    // empty). Duplicates within a tier are collapsed server-side.
    const websitePriceSlabs: Record<string, number[]> = {};
    const slabEntries = Object.entries(websiteSlabs);
    for (let i = 0; i < slabEntries.length; i += 1) {
      const [code, slabs] = slabEntries[i];
      const tierName = catalogProducts.find((p) => p.code === code)?.name ?? code;
      const parsed = slabs
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.round(n));
      if (parsed.length !== slabs.length) {
        toast.error(`${tierName}: every price option must be a positive amount`);
        return;
      }
      if (parsed.length === 0) {
        toast.error(`${tierName} needs at least one price option`);
        return;
      }
      websitePriceSlabs[code] = parsed;
    }

    update.mutate(
      {
        multiYearRenewalDiscountPercent: Math.round(parsedDiscount),
        products: [
          ...fixedProducts.map((p) => ({
            code: p.code,
            // Not Math.round (ADKJ-124): a product's price can be
            // fractional (e.g. "Website (AI based)" at 3093.2203), and
            // rounding it away here would clobber the whole catalogue entry
            // on the very next unrelated settings save.
            price: p.price,
            ...(multiYearByCode.has(p.code)
              ? { multiYearDiscountPercent: multiYearByCode.get(p.code) ?? null }
              : {}),
          })),
          ...tierRows,
        ],
        ...(Object.keys(websitePriceSlabs).length > 0 ? { websitePriceSlabs } : {}),
      },
      {
        onSuccess: () => toast.success('Product settings saved'),
        onError: (err) => toast.error(err.message || 'Could not save product settings'),
      },
    );
  };

  if (isLoading) {
    return <p className="text-sm text-ink/50">Loading product settings…</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Product Settings
          </h1>
          <p className="mt-1 text-sm text-ink/50">
            Manage the product catalogue, fixed prices, and multi-year renewal discount. AUM-based
            products use slab pricing on the Slabs page.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              setAddInitialCode('');
              setAddOpen(true);
            }}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add product
          </Button>
          <Button
            onClick={onSubmit}
            disabled={update.isPending}
            className="flex items-center gap-1.5"
          >
            <Save className="h-4 w-4" />
            {update.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>

      <Card className="mt-6 overflow-hidden p-0">
        <div className="border-b border-line px-5 py-4">
          <p className="text-sm font-bold text-ink">Products</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="border-b border-line bg-canvas">
              <tr>
                <th className={TH}>Product</th>
                <th className={TH}>Code</th>
                <th className={TH}>Pricing</th>
                <th className={`${TH} text-right`}>Annual price (₹)</th>
                <th className={`${TH} text-right`}>Upgrade tier</th>
                <th className={`${TH} text-right`}>Multi-year %</th>
                <th className={`${TH} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {catalogProducts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-sm text-ink/50">
                    No products yet.
                  </td>
                </tr>
              ) : (
                catalogProducts.map((product) => (
                  <tr key={product.code} className="hover:bg-canvas/60">
                    <td className={`${TD} font-medium text-ink`}>{product.name}</td>
                    <td className={`${TD} font-mono text-xs text-ink/50`}>{product.code}</td>
                    <td className={`${TD} text-sm text-ink/60`}>
                      {product.isAumBased ? 'AUM slabs' : 'Fixed'}
                    </td>
                    <td className={`${TD} text-right`}>
                      {product.isAumBased && <span className="text-sm text-ink/40">-</span>}
                      {/* API bundles are priced as the sum of the items the RM
                          selects, so the admin manages the item catalogue
                          instead of a single price. */}
                      {!product.isAumBased && isApiBundleProductCode(product.code) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setManageItemsCode(product.code)}
                          className="inline-flex items-center gap-1.5"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                          {`Manage items (${(data?.apiSubItems?.[product.code] ?? []).length})`}
                        </Button>
                      )}
                      {!product.isAumBased && !isApiBundleProductCode(product.code) && (
                        <PriceStepper
                          value={fixedPrices[product.code] ?? product.price}
                          onChange={(val) => setProductPrice(product.code, val)}
                          ariaLabel={`${product.name} price`}
                        />
                      )}
                    </td>
                    {/* Rank in the MFBOX upgrade ladder. Only AUM plans take
                        part - they are mutually exclusive alternatives, which
                        is what makes "move up a tier" meaningful. */}
                    <td className={`${TD} text-right`}>
                      {product.isAumBased ? (
                        <input
                          type="number"
                          min={1}
                          max={99}
                          step={1}
                          value={tiers[product.code] ?? ''}
                          onChange={(e) => setProductTier(product.code, e.target.value)}
                          placeholder="-"
                          aria-label={`${product.name} upgrade tier`}
                          className="w-16 rounded-md border border-line bg-white px-2 py-1.5 text-right text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        />
                      ) : (
                        <span className="text-sm text-ink/40">-</span>
                      )}
                    </td>
                    {/* Catalogue default for the years-2+ multi-year discount
                        (ADKJ-123). Meaningless for AUM products (slab-priced
                        renewal) and single-term products - both show "-". A
                        blank field follows the company-wide setting above. */}
                    <td className={`${TD} text-right`}>
                      {!product.isAumBased && getAllowedTermYears(product.code).some((y) => y > 1) ? (
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={multiYearDiscounts[product.code] ?? ''}
                          onChange={(e) => setProductMultiYearDiscount(product.code, e.target.value)}
                          placeholder={String(discountPercent)}
                          aria-label={`${product.name} multi-year discount percent`}
                          className="w-16 rounded-md border border-line bg-white px-2 py-1.5 text-right text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        />
                      ) : (
                        <span className="text-sm text-ink/40">-</span>
                      )}
                    </td>
                    <td className={`${TD} text-right`}>
                      <ConfirmButton
                        variant="ghost"
                        size="sm"
                        confirmText="Delete?"
                        disabled={remove.isPending}
                        onConfirm={() => onDeleteProduct(product.code, product.name)}
                        className="text-danger hover:bg-danger/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </ConfirmButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="mt-6 p-5">
        <div className="mb-4 border-b border-line pb-3">
          <p className="text-sm font-bold text-ink">MFTools multi-year discount</p>
        </div>
        <div className="flex max-w-xs flex-col gap-1">
          <span className="text-sm font-medium text-ink">Discount on years 2+ (%)</span>
          <NumberStepper
            value={Number(discountPercent) || 0}
            onChange={(v) => setDiscountPercent(String(v))}
            min={0}
            max={100}
            hideZero
            inputWidthClassName="w-14"
            placeholder="0"
            ariaLabel="Discount on years 2+"
            className="w-fit"
          />
          <p className="text-xs text-ink/60">
            Applies to flat/subscription products with multi-year terms (e.g. MFTools). Year 1 stays
            at full price.
          </p>
        </div>
      </Card>

      {/* Price options the wizard offers as chips for each website tier.
          WEBSITE_CUSTOM is absent by design - it is a free-text price. */}
      {Object.keys(websiteSlabs).length > 0 && (
        <Card className="mt-6 p-5">
          <div className="mb-4 border-b border-line pb-3">
            <p className="text-sm font-bold text-ink">Website price options</p>
            <p className="mt-0.5 text-xs text-ink/50">
              The Sales RM picks one of these amounts in the wizard. Custom Development stays
              free-text and has no options here.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {Object.entries(websiteSlabs).map(([code, slabs]) => {
              const tierName = catalogProducts.find((p) => p.code === code)?.name ?? code;
              return (
                <div key={code} className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-ink">{tierName}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    {slabs.map((slab, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div
                        key={`${code}-${index}`}
                        className="inline-flex items-center rounded-lg border border-line bg-white shadow-sm"
                      >
                        <input
                          type="number"
                          min={0}
                          step={500}
                          value={slab}
                          onChange={(e) => setSlabValue(code, index, e.target.value)}
                          aria-label={`${tierName} price option ${index + 1}`}
                          className="w-24 border-none bg-transparent py-1.5 pl-3 text-sm font-medium text-ink focus:outline-none focus:ring-0"
                        />
                        <button
                          type="button"
                          onClick={() => removeSlab(code, index)}
                          aria-label={`Remove ${tierName} price option ${index + 1}`}
                          className="flex h-8 w-7 items-center justify-center rounded-r-lg text-ink/40 transition-colors hover:bg-danger/10 hover:text-danger"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSlab(code)}
                      className="inline-flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add price
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
            <p className="text-xs text-ink/50">
              Options are saved with the Save settings button above.
            </p>
            {/* A new tier is a new PRODUCT, not just another options row: it has
                to be sellable in the wizard before its prices mean anything.
                The dialog is prefilled with the WEBSITE_ prefix, which is what
                makes it behave as a website tier. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAddInitialCode(WEBSITE_PRODUCT_CODE_PREFIX);
                setAddOpen(true);
              }}
              className="inline-flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Add website tier
            </Button>
          </div>
        </Card>
      )}

      <AddProductDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        initialCode={addInitialCode}
        bandTemplate={bandTemplate}
        // So the dialog can require a tier and show which rungs are already
        // taken, rather than making the admin discover clashes by trial.
        aumProducts={data?.aumProducts ?? []}
      />

      {manageItemsCode !== null && (
        <ApiSubItemsDialog
          open
          onOpenChange={(open) => {
            if (!open) setManageItemsCode(null);
          }}
          productCode={manageItemsCode}
          productName={
            catalogProducts.find((p) => p.code === manageItemsCode)?.name ?? manageItemsCode
          }
          items={data?.apiSubItems?.[manageItemsCode] ?? []}
        />
      )}
    </div>
  );
};

export default AdminPriceSettingsPage;
